import { ApiError } from '@/lib/api/api-client'
import {
  captureIdentityScope,
  captureRequestScope,
} from '@/lib/api/request-scope'
import { useAuth } from '@/store/auth.store'

import { createOwnedRestore } from '@scrambl/shared/offline'

import type { OfflineRunnerContext } from './offline-queue'
import { createOwnedOfflineLedger, createSerialGate } from './offline-queue'
import { createOwnedGate } from './owned-work'

const persistence = createOwnedOfflineLedger(
  {
    getItem: async key => window.localStorage.getItem(key),
    setItem: async (key, value) => {
      window.localStorage.setItem(key, value)
    },
    removeItem: async key => {
      window.localStorage.removeItem(key)
    },
  },
  'scrambl-offline-write-ledger-v1',
)
export const offlineLedger = persistence.ledger
export const flushOfflineLedger = persistence.flush
export const restoreOfflineLedger = createOwnedRestore({
  capture: captureIdentityScope,
  ownerId: () => useAuth.getState().user?.id ?? null,
  restore: persistence.restore,
})

// Keep each account's pending work while only restoring its own outbox.
// Optional call: tests mock the auth store as a plain function with no
// subscribe, and importing this module must not take the suite down.
useAuth.subscribe?.((state, previous) => {
  if (state.user?.id !== previous.user?.id) {
    void restoreOfflineLedger().catch(() =>
      console.warn('[OfflineQueue] Restore could not be confirmed'),
    )
  }
})

export function getErrorStatus(error: unknown): number | undefined {
  if (error instanceof ApiError) return error.status
  const status = (error as { status?: unknown } | null)?.status
  return typeof status === 'number' ? status : undefined
}

export const offlineRunnerContext: OfflineRunnerContext = {
  captureSession: captureRequestScope,
  flush: flushOfflineLedger,
  getOwnerId: () => useAuth.getState().user?.id ?? null,
  ledger: offlineLedger,
  gate: createOwnedGate(createSerialGate(), captureRequestScope),
  getStatus: getErrorStatus,
}
